import { escapeRegExp } from "lodash";
import moment from "moment";
import { DEFAULT_TABLE_PAGE_SIZE } from "src/constants/constants";

export const createSearchRegExp = (searchQuery?: string) =>
  new RegExp(escapeRegExp(searchQuery), "i");

/**
 * combine filter, sort and pagination into one function
 */
export const filterSortAndPaginateTableData = <T extends object>({
  tableData,
  searchField,
  sortColumn,
  sortDirection,
  currentPage,
  columnsToSkip,
  pageSize = DEFAULT_TABLE_PAGE_SIZE,
  sortStrategy = SortStrategy.DEFAULT,
  columnsToConditionallySkip,
}: {
  tableData: T[];
  searchField: string;
  sortColumn: keyof T;
  sortDirection: string;
  currentPage: number;
  pageSize?: number;
  columnsToSkip?: (keyof T)[];
  sortStrategy?: SortStrategy;
  columnsToConditionallySkip?: [string, (x: any) => boolean][];
}) => {
  const filteredData = filterTableData({
    tableData,
    searchField,
    columnsToSkip,
    columnsToConditionallySkip,
  });
  const sortedData = sortBasedOnStrategy({
    tableData: filteredData,
    sortColumn,
    sortDirection,
  });
  return paginateTableData({ tableData: sortedData, currentPage, pageSize });
};

export enum SortStrategy {
  DEFAULT,
  NUMBER,
  DATE,
}

/**
 * perform universal filter on all table columns given search field
 */
export const filterTableData = <T extends { [key: string]: any }>({
  tableData,
  searchField,
  columnsToSkip = [],
  columnsToConditionallySkip,
}: {
  tableData: T[];
  searchField: string;
  columnsToSkip?: (keyof T)[];
  columnsToConditionallySkip?: [string, (x: T) => boolean][];
}) => {
  const searchRegExp = createSearchRegExp(searchField as string);
  return tableData.filter((row) =>
    Object.keys(row)
      .map((key) => {
        return (
          !columnsToSkip.includes(key) &&
          !columnsToConditionallySkip?.some(
            ([columnName, skipFunction]) =>
              columnName === key && skipFunction(row),
          ) &&
          row[key] !== undefined &&
          searchRegExp.test(serializeValue(row[key]))
        );
      })
      .reduce(
        (prevValue: boolean, currValue: boolean) => prevValue || currValue,
      ),
  );
};

/**
 * paginate table rows given current page
 */
export const paginateTableData = <T extends object>({
  tableData,
  currentPage,
  pageSize = DEFAULT_TABLE_PAGE_SIZE,
}: {
  tableData: T[];
  currentPage: number;
  pageSize: number;
}) => {
  const totalPages = Math.ceil(tableData.length / pageSize);
  const startIndex = pageSize * (currentPage - 1);
  return {
    paginatedTableData: tableData.slice(startIndex, startIndex + pageSize),
    totalPages,
  };
};

/**
 * serialize value for filter
 */
const serializeValue = (value: any) => {
  if (typeof value === "boolean") {
    return value === true ? "Yes" : "No";
  }
  return value;
};

export const sortBasedOnStrategy = <T extends object>({
  sortColumn,
  sortDirection,
  tableData,
}: {
  tableData: T[];
  sortColumn: keyof T;
  sortDirection: string;
}) => {
  if (!tableData.length) return [];

  if (tableData[0][sortColumn] instanceof Date) {
    return dateStrategy({ sortColumn, sortDirection, tableData });
  } else if (typeof tableData[0][sortColumn] === "number") {
    return numberStrategy({ sortColumn, sortDirection, tableData });
  } else {
    return defaultStrategy({ sortColumn, sortDirection, tableData });
  }
};

const defaultStrategy = <T extends object>({
  tableData,
  sortColumn,
  sortDirection,
}: {
  tableData: T[];
  sortColumn: keyof T;
  sortDirection: string;
}) => {
  return tableData.sort((a: T, b: T) => {
    const aValue = a[sortColumn]?.toString().toLowerCase() || "";
    const bValue = b[sortColumn]?.toString().toLowerCase() || "";

    if (aValue === bValue) {
      return 0;
    }

    // Null or undefined values are positioned
    // at the start when sorted descending
    // and at the end when sorted ascending
    if (!aValue) {
      return sortDirection === "ascending" ? 1 : -1;
    }
    if (!bValue) {
      return sortDirection === "ascending" ? -1 : 1;
    }

    return sortDirection === "ascending"
      ? aValue.localeCompare(bValue)
      : bValue.localeCompare(aValue);
  });
};

const numberStrategy = <T extends object>({
  tableData,
  sortColumn,
  sortDirection,
}: {
  tableData: T[];
  sortColumn: keyof T;
  sortDirection: string;
}) => {
  return tableData.sort((a: T, b: T) => {
    let aValue = +a[sortColumn];
    let bValue = +b[sortColumn];

    if (aValue < bValue) {
      return sortDirection === "ascending" ? -1 : 1;
    } else if (aValue > bValue) {
      return sortDirection === "ascending" ? 1 : -1;
    }

    return 0;
  });
};

const dateStrategy = <T extends object>({
  tableData,
  sortColumn,
  sortDirection,
}: {
  tableData: T[];
  sortColumn: keyof T;
  sortDirection: string;
}) => {
  return tableData?.sort((a: T, b: T) => {
    let aValue = moment(a[sortColumn]);
    let bValue = moment(b[sortColumn]);

    if (aValue.isBefore(bValue)) {
      return sortDirection === "ascending" ? -1 : 1;
    } else if (aValue.isAfter(bValue)) {
      return sortDirection === "ascending" ? 1 : -1;
    }

    return 0;
  });
};
